import { useEffect, useState } from 'react';

/**
 * Phone-width breakpoint. Kept in sync with the web shell
 * (`ProtectedWebShell.tsx`) and the `@media (max-width: 767px)` rules in
 * `global.css` so JS-driven layout switches land on the same line as CSS ones.
 */
export const MOBILE_BREAKPOINT = 768;
const MOBILE_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 1}px)`;

function readMatch(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(MOBILE_QUERY).matches;
}

/**
 * `true` while the viewport is phone-width. Resolves synchronously on first
 * render so a layout that branches on it never flashes the wrong variant.
 *
 * Web-only question, so it answers `false` on iOS/Android (no `matchMedia`).
 * Native screens pick their presentation from the navigator, not a breakpoint.
 */
export function useIsMobileViewport(): boolean {
  const [isMobile, setIsMobile] = useState(readMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setIsMobile(mq.matches);
    // Re-read on mount: the first render may have happened before hydration.
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}
